/**
 * The usage coverage notice, shared by the Usage page and the organization's
 * month card in Settings.
 *
 * @module components/usage/UsageCoverageNotice
 */

/**
 * Says plainly when the totals are incomplete: an environment still answering,
 * one that failed, one whose ledger another environment already reported, or a
 * read that could not cover the whole window.
 */
export function UsageCoverageNotice({
  environments,
  duplicateSources,
  staleEnvironments,
  coverageNotices,
  isPartial,
}: {
  readonly environments: readonly {
    environmentId: string;
    label: string;
    error: string | null;
    isPending: boolean;
  }[];
  readonly duplicateSources: readonly string[];
  readonly staleEnvironments: readonly string[];
  readonly coverageNotices: readonly string[];
  readonly isPartial: boolean;
}) {
  const failed = environments.filter((environment) => environment.error !== null);
  const stale = environments.filter((environment) =>
    staleEnvironments.includes(environment.environmentId),
  );
  if (
    failed.length === 0 &&
    stale.length === 0 &&
    duplicateSources.length === 0 &&
    coverageNotices.length === 0 &&
    !isPartial
  ) {
    return null;
  }

  return (
    <div className="flex flex-col gap-1 border border-border px-3 py-2 text-xs text-muted-foreground">
      {isPartial ? <span>Some environments are still reporting. Totals are partial.</span> : null}
      {failed.map((environment) => (
        <span key={environment.environmentId}>{environment.label} could not report usage.</span>
      ))}
      {stale.map((environment) => (
        <span key={environment.environmentId}>
          {environment.label} runs an older server version and is excluded from totals.
        </span>
      ))}
      {coverageNotices.map((notice) => (
        <span key={notice}>{notice}</span>
      ))}
      {duplicateSources.length > 0 ? (
        <span>
          Counted once across environments sharing an Amoreg database: {duplicateSources.join(", ")}
        </span>
      ) : null}
    </div>
  );
}
